import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { action, computed, observable, reaction } from 'mobx'
import {
  m,
  type MotionValue,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
} from 'motion/react'
import type { SpringOptions } from 'motion-dom'
import styles from './ModalDialog.module.css'

/**
 * Manages entries in the modal stack.
 * Used for maintaining modal stack layer order.
 */
export class ModalStackManager {
  /**
   * Modal entries.
   * @private
   */
  @observable private accessor _stack: ModalStackEntry[] = []

  /**
   * Logical clock used for determining the order in which modals are shown.
   *
   * @private
   */
  @observable private accessor _clock = 0

  /**
   * Entries in the stack that are currently showing.
   * Sorted by their clock values in ascending order.
   * That means the modal that is shown first is returned first.
   */
  @computed
  get showingEntries() {
    const filtered = this._stack.filter((x) => x.showing)
    return filtered.sort((a, b) => a.clock - b.clock)
  }

  /**
   * Returns the layer (position in the stack) for the entry.
   * The top layer is 0.
   */
  getLayerForEntry(entry: ModalStackEntry) {
    const showing = this.showingEntries
    const idx = showing.indexOf(entry)
    if (idx === -1) {
      return 0
    }

    return showing.length - idx - 1
  }

  /**
   * Advances the clock and returns it.
   */
  @action.bound
  tick() {
    this._clock++
    return this._clock
  }

  /**
   * Adds an entry.
   */
  @action.bound
  add() {
    const entry = new ModalStackEntry(this)
    this._stack.push(entry)
    return entry
  }

  /**
   * Removes the entry
   * @param entry
   */
  @action.bound
  removeEntry(entry: ModalStackEntry) {
    const idx = this._stack.indexOf(entry)
    if (idx > -1) {
      this._stack.splice(idx, 1)
    }
  }
}

/**
 * An entry in the modal stack.
 */
export class ModalStackEntry {
  /**
   * Tracks whether the backing modal is showing.
   */
  @observable accessor showing = false

  /**
   * Whether the modal is being shown as a bottom sheet.
   */
  @observable accessor isSheet = false

  /**
   * Determines the order in which modals are shown.
   */
  @observable accessor clock = 0

  /**
   * Constructor.
   * @param _manager
   */
  constructor(private readonly _manager: ModalStackManager) {}

  /**
   * Get the layer position for this entry.
   */
  @computed
  get layer() {
    return this._manager.getLayerForEntry(this)
  }

  /**
   * Updates the props.
   *
   * @param showing
   * @param isSheet
   */
  @action.bound
  update(showing: boolean, isSheet: boolean) {
    this.showing = showing
    this.isSheet = isSheet
    if (showing) {
      this.clock = this._manager.tick()
    }
  }
}

// Spring used for stacking animations.
const stackingSpring: SpringOptions = {
  damping: 90,
  stiffness: 1200,
}

/**
 * The stack manager.
 */
const ModalStackContext = createContext<ModalStackManager | null>(null)

/**
 * Props for {@link ModalStackContextProvider}.
 */
interface ModalStackContextProviderProps {
  children?: ReactNode
}

/**
 * Instantiates and passes down a modal stack manager.
 */
export function ModalStackContextProvider({
  children,
}: ModalStackContextProviderProps) {
  const [isShowingSheet, setIsShowingSheet] = useState(false)
  const ref = useRef<ModalStackManager>(null)
  ref.current ??= new ModalStackManager()

  const manager = ref.current
  const bodyLayer = useMotionValue(0)
  const bodyLayerSpring = useSpring(bodyLayer, stackingSpring)

  useEffect(() => {
    // We'll only apply the body stacking effect if we're showing any sheets.
    const unsubscribe = reaction(
      () => manager.showingEntries.filter((e) => e.isSheet).length,
      (showingCount) => {
        bodyLayer.set(showingCount)
      },
      { fireImmediately: true },
    )

    return () => {
      bodyLayer.set(0)
      unsubscribe()
    }
  }, [bodyLayer, manager])

  // Update the chrome theme color when the modal is open.
  useMotionValueEvent(bodyLayerSpring, 'change', (v) => {
    setIsShowingSheet(v > 0)
  })

  return (
    <ModalStackContext.Provider value={manager}>
      {children}

      {isShowingSheet && <OverlayMask layerSpring={bodyLayerSpring} />}
    </ModalStackContext.Provider>
  )
}

interface OverlayMaskProps {
  layerSpring: MotionValue<number>
}

/**
 * A mask that overlays the main content when a modal sheet is open.
 *
 * @param layerSpring
 * @param themeColor
 * @constructor
 */
function OverlayMask({ layerSpring }: OverlayMaskProps) {
  const maskBlur = useTransform(layerSpring, [0, 1], [0, 10])
  const backdropFilter = useMotionTemplate`blur(${maskBlur}px)`

  return (
    <m.div
      className={styles.mask}
      style={{
        backdropFilter,
      }}
    />
  )
}

/**
 * Gets the modal stack manager.
 */
export function useModalStackManager() {
  const manager = useContext(ModalStackContext)
  if (!manager) {
    throw new Error('Missing modal stack manager context')
  }

  return manager
}

////////////
// Modal stacking hook
////////////

interface UseModalStackingProps {
  isOpen: boolean
  isSheet: boolean
}

/**
 * Manages motion value for the modal layer, used for animating the modal
 * dialog based on the modal stacking context.
 */
export function useModalStacking({ isOpen, isSheet }: UseModalStackingProps) {
  const manager = useModalStackManager()
  const layer = useMotionValue(0)

  const entryRef = useRef<ModalStackEntry>(null)

  useEffect(() => {
    if (entryRef.current) {
      return
    }

    const entry = manager.add()
    entryRef.current = entry

    const unsubscribe = reaction(
      () => entry.layer,
      (l) => layer.set(l),
    )

    return () => {
      unsubscribe()
      manager.removeEntry(entry)
      entryRef.current = null
    }
  }, [layer, manager])

  useEffect(() => {
    if (!entryRef.current) {
      return
    }

    entryRef.current.update(isOpen, isSheet)
  }, [isOpen, isSheet])

  const stackingY = useSpring(
    useTransform(layer, (l): number => {
      // Once we stack beyond 2, start pushing them down.
      switch (l) {
        case 0:
          return 0
        case 1:
          return -24
        case 2:
          return 0
        default:
          return 48 * (l - 2)
      }
    }),
    stackingSpring,
  )

  const stackingScale = useSpring(
    useTransform(layer, (l) => 1 - l * 0.06),
    stackingSpring,
  )

  const stackingTransform = useMotionTemplate`translateY(${stackingY}px) scale(${stackingScale})`

  return {
    layer,
    stackingTransform,
  }
}

/**
 * Checks whether a modal is showing.
 */
export function useIsShowingModal() {
  const manager = useModalStackManager()
  const read = useCallback(
    () => Boolean(manager.showingEntries.length),
    [manager],
  )
  const [isShowingModal, setIsShowingModal] = useState(read)
  useEffect(() => reaction(read, setIsShowingModal), [read])
  return isShowingModal
}
